/**
 * Admin V3 — Bookings — per-row dispatch controls (HIDE-RBAC)
 *
 * Two actions on an unassigned booking, reusing the EXISTING backend endpoints
 * (no backend change), each idempotent (useAdminMutation auto-sends
 * X-Idempotency-Key, matching the Phase-10B contract):
 *   - Auto-assign → POST /admin/bookings/{id}/auto-assign (empty body)
 *       RBAC: can('admin:bookings:auto_assign'). Dispatch Engine picks the
 *       nearest available driver; failure (400) surfaces the backend detail
 *       ("No available drivers" / "Cannot auto-assign: <reason>").
 *   - Manual assign → POST /admin/bookings/{id}/assign { driver_id }
 *       RBAC: can('admin:bookings:assign'). Options from the page-level
 *       /admin/drivers/available fetch (passed as prop).
 *
 * A native <select> is used for the driver dropdown (parity with V1, and
 * deterministically testable). Each action refetches the queue via onDone.
 */

import React from 'react';
import { Zap, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';

const PERM_AUTO_ASSIGN = 'admin:bookings:auto_assign';
const PERM_ASSIGN = 'admin:bookings:assign';

const BookingDispatchControls = ({ booking, availableDrivers = [], onDone, locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const { showSuccess, showError } = useToast();

  const id = booking?.id || '_';
  const auto = useAdminMutation(`/admin/bookings/${id}/auto-assign`, 'post');
  const assign = useAdminMutation(`/admin/bookings/${id}/assign`, 'post');

  const canAuto = can(PERM_AUTO_ASSIGN);
  const canAssign = can(PERM_ASSIGN);
  const busy = auto.loading || assign.loading;

  // No permission for either action → render nothing (fail-closed HIDE).
  if (!canAuto && !canAssign) return null;

  const handleAuto = () => {
    auto.mutate(
      {},
      {
        onSuccess: (res) => {
          showSuccess(res?.message || t('dispatch.autoAssign.success', locale));
          if (onDone) onDone();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('dispatch.error.autoAssign', locale)),
      },
    );
  };

  const handleAssign = (driverId) => {
    if (!driverId) return;
    assign.mutate(
      { driver_id: driverId },
      {
        onSuccess: () => {
          showSuccess(t('dispatch.assign.success', locale));
          if (onDone) onDone();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('dispatch.error.assign', locale)),
      },
    );
  };

  return (
    <div className="flex items-center gap-2" data-testid={`adminv3-dispatch-${id}`}>
      {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}

      {canAuto && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={handleAuto}
          className="h-9 rounded-xl border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
          data-testid={`adminv3-dispatch-auto-${id}`}
        >
          <Zap className="h-3.5 w-3.5 mr-1.5" />
          {t('dispatch.autoAssign.button', locale)}
        </Button>
      )}

      {canAssign && (
        <select
          value=""
          disabled={busy}
          onChange={(e) => { handleAssign(e.target.value); e.target.value = ''; }}
          className="h-9 w-44 rounded-xl border border-slate-200 bg-white px-2 text-xs text-slate-700 cursor-pointer outline-none focus:border-slate-400"
          data-testid={`adminv3-dispatch-assign-${id}`}
          aria-label={t('dispatch.assign.placeholder', locale)}
        >
          <option value="">{t('dispatch.assign.placeholder', locale)}</option>
          {availableDrivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name || d.id}{d.vehicle_model ? ` · ${d.vehicle_model}` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export { PERM_AUTO_ASSIGN, PERM_ASSIGN };
export default BookingDispatchControls;
